import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { membershipService } from '../services/membershipService'
import type { MembershipFilters, MembershipOverrideRequest } from '../types/membership'

export function useMyMembership() {
  return useQuery({
    queryKey: ['membership', 'me'],
    queryFn: async () => {
      const response = await membershipService.getMyMembership()
      return response?.data ?? null
    },
  })
}

export function useAdminMemberships(filters?: MembershipFilters) {
  return useQuery({
    queryKey: ['admin', 'memberships', filters],
    queryFn: async () => {
      const response = await membershipService.listMemberships(filters)
      return { data: response?.data ?? [], meta: response?.meta }
    },
  })
}

export function useMembershipHistory(userId: string) {
  return useQuery({
    queryKey: ['admin', 'membership-history', userId],
    queryFn: async () => {
      const response = await membershipService.getMembershipHistory(userId)
      return response?.data ?? []
    },
    enabled: !!userId,
  })
}

export function useOverrideTier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, request }: { userId: string; request: MembershipOverrideRequest }) =>
      membershipService.overrideTier(userId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'memberships'] })
      queryClient.invalidateQueries({ queryKey: ['membership', 'me'] })
    },
  })
}

export function useRecalculateMemberships() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => membershipService.recalculateAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'memberships'] })
    },
  })
}

export function useMembershipThresholds() {
  return useQuery({
    queryKey: ['admin', 'membership-thresholds'],
    queryFn: async () => {
      const response = await membershipService.getThresholds()
      return response?.data ?? null
    },
  })
}

export function useMyThresholds() {
  return useQuery({
    queryKey: ['membership', 'my-thresholds'],
    queryFn: async () => {
      const response = await membershipService.getMyThresholds()
      return response?.data ?? null
    },
  })
}

export function useUpdateMembershipThresholds() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (thresholds: {
      bronze: number; bronze_period: 'alltime' | 'yearly'
      silver: number; silver_period: 'alltime' | 'yearly'
      gold: number; gold_period: 'alltime' | 'yearly'
      platinum: number; platinum_period: 'alltime' | 'yearly'
      vip: number; vip_period: 'alltime' | 'yearly'
    }) =>
      membershipService.updateThresholds(thresholds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'membership-thresholds'] })
    },
  })
}
